import { Component, Injector, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { BaseComponent } from 'src/app/common/base/base.component';
import { Document } from 'src/app/services/documents/documents';
import { AffectedForm, RadSurveyChecklist } from '../restrictions/services/restriction';
import { FormStatus } from 'src/app/services/forms/form-status';
import { RadSurveyChecklists } from 'src/app/components/kenable/ke-main/ke-sectionX/ke-sectionX.component';

@Component({
  selector: 'document-chips',
  templateUrl: './document-chips.component.html',
  styleUrls: ['./document-chips.component.scss']
})
export class DocumentChipsComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() documentChips?: DocumentChip[] | AffectedForm[];
  @Input() shadow?: boolean;
  @Input() isRadSurvey?: boolean;
  @Input() staticDisplay?: boolean;

  chips: DocumentChip[] = [];
  noChipsLabel?: string;

  formStatus?: FormStatus;
  formStatuses?: FormStatus[];
  formStatusesSubs!: Subscription;

  constructor(
    protected override injector: Injector
  ) {
    super(injector);
  }

  ngOnInit() {
    this.loadFormStatuses();
    this.noChipsLabel = !Array.isArray(this.documentChips) ? this.documentChips : undefined;
  }

  /**
   * Rebuilds chips when the bound documents change after initialization, such as after a live restriction refresh.
   * @param changes The changed input bindings.
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['documentChips'] && !changes['documentChips'].firstChange && this.formStatuses) {
      this.buildChips();
    }
  }

  loadFormStatuses() {
    this.formStatusesSubs = this.store.select(state => state.FormStatuses.data).subscribe(data => {
      if (data?.length) {
        this.formStatuses = data;
        this.buildChips();
      }
    });
  }

  private buildChips() {
    if (Array.isArray(this.documentChips)) {
      if (isDocumentChipArray(this.documentChips)) {
        this.chips = this.documentChips;
      } else if (isAffectedFormArray(this.documentChips)) {
        this.chips = this.affectedFormsToDocumentChips(this.documentChips);
      }
    } else {
      this.noChipsLabel = this.documentChips;
    }
  }

  openDocument(e: string) {
    window.location.href = "#/redirect/" + e;
  }

  affectedFormsToDocumentChips(affectedForms: AffectedForm[]): DocumentChip[] {
    const chips = [] as DocumentChip[];
    affectedForms.forEach(a => {
      const s = this.formStatuses?.find(f => f.id === a.formStatus);
      chips.push({
        id: a.formID!,
        serialNo: a.serialNo!,
        status: s?.name ?? '',
        cssClass: ((s?.color ?? '')),
        documentChips: this.radSurveyChecklistsToDocumentChips(a.radSurveyChecklists)
      });
    });
    return chips;
  }

  radSurveyChecklistsToDocumentChips(radSurveyChecklists: RadSurveyChecklist[]): DocumentChip[] {
    const chips = [] as DocumentChip[];
    radSurveyChecklists?.forEach(r => {
      chips.push({
        id: r.id,
        serialNo: r.serialNo,
        status: r.checklistStatus.name ?? '',
        cssClass: r.checklistStatus.cssClass ?? '',
      });
    });
    return chips;
  }
}

export interface DocumentChip {
  id: number;
  serialNo: string;
  status: string;
  cssClass: string;
  documentChips?: DocumentChip[];
}

function isDocumentChipArray(arr: unknown[]): arr is DocumentChip[] {
  return arr.every(x =>
    !!x &&
    typeof x === 'object' &&
    'id' in x &&
    'serialNo' in x &&
    'status' in x &&
    'cssClass' in x &&
    !('formID' in x)
  );
}

function isAffectedFormArray(arr: unknown[]): arr is AffectedForm[] {
  return arr.every(x =>
    !!x &&
    typeof x === 'object' &&
    'formID' in x &&
    'serialNo' in x &&
    'formStatus' in x
  );
}
